import React from 'react';

import { Glossary, BoardGL } from 'common/utils/glossary';
import {
  Button,
  Message,
  Notes,
  LinkText,
  Checkbox,
  Separator
} from 'common/components';
import { SubButtonType } from 'common/components/button';

interface BeforePurchaseBoardProps {
  /** PurchaseConfirmationBoardの表示有無 */
  isDisplay: boolean;
  /** グロッサリ */
  glossary: Glossary;
  /** "クローズボタン"の押下処理 */
  onClickClose: () => void;
  /** "利用規約を確認"の押下処理 */
  onClickTermsLink: () => void;
  /** "購入手続きへ進む"の押下処理 */
  onClickMoveToPurchase: () => void;
}

interface CheckBoxState {
  /** 購入前注記の同意にチェックしたか否か */
  agreeNotes: boolean;
  /** 利用条件の同意にチェックしたか否か */
  agreeTerms: boolean;
}

/**
 * 購入確認画面のPresentation Component
 */
export const BeforePurchaseBoard: React.FC<BeforePurchaseBoardProps> = ({
  isDisplay,
  glossary,
  onClickClose,
  onClickTermsLink,
  onClickMoveToPurchase
}) => {
  const initCheckedState = {
    agreeNotes: false,
    agreeTerms: false
  };
  /**  チェック状態の定義と変更 */
  const [checkedState, setCheckedState] = React.useState<CheckBoxState>(
    initCheckedState
  );

  /** 購入前注記のチェックボックス押下時の処理 */
  const onClickNotesCheckBox = () => {
    setCheckedState({ ...checkedState, agreeNotes: !checkedState.agreeNotes });
  };

  /** 利用条件のチェックボックス押下時の処理 */
  const onClickTermsCheckBox = () => {
    setCheckedState({ ...checkedState, agreeTerms: !checkedState.agreeTerms });
  };

  /** チェック状態によって購入ボタンのサブタイプを制御 */
  let subButtonType: SubButtonType = 'invalid';
  if (checkedState.agreeNotes && checkedState.agreeTerms) {
    subButtonType = 'highPriority';
  }

  /** 表示時にチェックボックスをチェックなしにする */
  React.useEffect(() => {
    setCheckedState(initCheckedState);
  }, [isDisplay]);

  const _component = (
    <div className="beforePurchaseBoard" data-test="beforePurchaseBoard">
      <Button
        type="toolButton"
        subType="close"
        data-test="beforePurchaseBoard-toolButton"
        onClick={onClickClose}
      />
      <Message type="message" data-test="beforePurchaseBoard-message">
        {glossary.glx(BoardGL.BeforePurchaseBoardTitle)}
      </Message>
      <Separator type="separator" data-test="BeforePurchaseBoardSeparator1" />
      <Notes
        notes={[glossary.glx(BoardGL.BeforePurchaseBoardNote)]}
        data-test={`beforePurchaseBoard-notes`}
      />
      <div className="beforePurchaseBoard-agreement">
        <Checkbox
          id="id_NotesCheckBox"
          value="notesAgreement"
          checked={checkedState.agreeNotes}
          onChange={onClickNotesCheckBox}
          data-test="beforePurchaseBoard-checkbox"
        />
        <LinkText
          type="checkBoxLabel"
          className="agreement-label"
          onClick={onClickNotesCheckBox}
          data-test="beforePurchaseBoard-agreementLabel"
        >
          {glossary.glx(BoardGL.PurchaseNotesAgreementCheckboxLabel)}
        </LinkText>
      </div>
      <Separator type="separator" data-test="BeforePurchaseBoardSeparator2" />
      <LinkText
        type="termsLink"
        data-test="beforePurchaseBoard-termsLink"
        onClick={onClickTermsLink}
      >
        {glossary.glx(BoardGL.ConfirmationTermsOfService)}
      </LinkText>
      <div className="beforePurchaseBoard-agreement">
        <Checkbox
          id="id_termsAgreement"
          value="termsAgreement"
          checked={checkedState.agreeTerms}
          onChange={onClickTermsCheckBox}
          data-test="beforePurchaseBoard-termsCheckbox"
        />
        <LinkText
          type="checkBoxLabel"
          className="agreement-label"
          onClick={onClickTermsCheckBox}
          data-test="beforePurchaseBoard-termsAgreementLabel"
        >
          {glossary.glx(BoardGL.PurchaseTermsAgreementCheckboxLabel)}
        </LinkText>
      </div>
      <Separator type="separator" data-test="BeforePurchaseBoardSeparator3" />
      <Button
        type="nextButton"
        subType={subButtonType}
        data-test="beforePurchaseBoard-nextButton"
        onClick={onClickMoveToPurchase}
      >
        {glossary.glx(BoardGL.GoToPurchase)}
      </Button>
    </div>
  );
  return isDisplay ? _component : null;
};
